import type { FC } from 'react';
import React, { useMemo } from 'react';
import cn from 'classnames';

import type { TX5BankHeaderV2Desktop } from '../models';

export const X5BankHeaderV2Desktop: FC<TX5BankHeaderV2Desktop> = ({
    Button,
    CommonLink,
    IconChevronUp,
    Img,
    Layout,
    MainMenuLink,
    StickyHeader,
    additionalButtonSticky,
    additionalButtonTop,
    buttonToTop,
    dataTestId = 'X5BankHeaderDesktop',
    dropDown,
    logos,
    menu,
    pageId,
    styles,
    theme = 'light',
    useStickyHeader = true,
    logoStyler = {},
}) => {
    const colorTheme = cn(styles[theme], styles.external);

    const handleTop = () => {
        window.scrollTo({
            top: 0,
        });
    };

    const renderMainMenuLinks = useMemo(
        () =>
            menu.map(({ key, link, title }, index) => {
                const subLink = dropDown?.[key]?.find(({ key: keyLink }) => keyLink === pageId);
                const isActive = subLink?.key === pageId;

                return (
                    <MainMenuLink
                        key={`${title}_${link}`}
                        dataTestId={`${dataTestId}-mainMenuLink-${index}`}
                        dropDownData={dropDown?.[key]}
                        isActive={key === pageId || isActive}
                        link={link}
                        pageId={pageId}
                        theme={theme}
                        title={title}
                    />
                );
            }),
        [menu, MainMenuLink, dropDown, pageId, theme, dataTestId]
    );

    const classNames = {
        layout: cn(styles.layout, styles.external, styles[theme]),
        layoutContent: cn(styles.layoutContent, styles.external, styles[theme]),
    };

    const logoHeight = logos[theme].height || 32;
    const logoWidth = logos[theme].width || 110;

    return (
        <>
            <Layout
                className={colorTheme}
                classNames={classNames}
                dataTestId={`${dataTestId}-desktopLayout`}
            >
                <span style={{ height: logos[theme].height }}>
                    <CommonLink dataTestId={`${dataTestId}-bannerLink`} link="/">
                        <Img
                            alt={logos[theme].title}
                            height={logoHeight}
                            src={logos[theme].link}
                            styler={logoStyler}
                            width={logoWidth}
                        />
                    </CommonLink>
                </span>
                <div style={{ display: 'flex' }}>
                    <ul className={styles.mainMenuContainer}>{renderMainMenuLinks}</ul>
                    {additionalButtonTop && (
                        <Button
                            dataTestId={`${dataTestId}-buttonToPersonnelCabinet`}
                            href={additionalButtonTop.href}
                            innerClassName={cn(
                                styles.button,
                                styles.buttonAdditional,
                                styles.external,
                                styles[theme]
                            )}
                            size="s"
                            view="primary"
                        >
                            {additionalButtonTop.title}
                        </Button>
                    )}
                </div>
            </Layout>
            {useStickyHeader && (
                <StickyHeader dataTestId={`${dataTestId}-stickyHeaderDesktop`} theme={theme}>
                    <span style={{ height: logos[theme].height }}>
                        <CommonLink
                            dataTestId={`sticky-${dataTestId}-bannerLink`}
                            link="/"
                            title={logos.lightInverted?.title}
                        >
                            <Img
                                alt={logos[theme].title}
                                height={logoHeight}
                                src={logos[theme].link}
                                width={logoWidth}
                            />
                        </CommonLink>
                    </span>
                    <div className={styles.buttonsContainer}>
                        <Button
                            dataTestId={`${dataTestId}-buttonToTop`}
                            innerClassName={cn(
                                styles.button,
                                styles.buttonScrollTop,
                                styles.external,
                                styles[theme]
                            )}
                            // @ts-ignore
                            // !TODO: придумать как прокинуть иконку слева
                            leftAddons={<IconChevronUp />}
                            onClick={handleTop}
                            size="s"
                        >
                            {buttonToTop?.title}
                        </Button>
                        {additionalButtonSticky && (
                            <Button
                                dataTestId={`${dataTestId}-buttonAdditional`}
                                href={additionalButtonSticky.href}
                                innerClassName={cn(
                                    styles.button,
                                    styles.buttonAdditional,
                                    styles.external,
                                    styles[theme]
                                )}
                                size="s"
                                view="primary"
                            >
                                {additionalButtonSticky.title}
                            </Button>
                        )}
                    </div>
                </StickyHeader>
            )}
        </>
    );
};
